import { money, fmtDate } from "@/lib/format";

const STATUS_LABEL = { vigente: "Vigente", convertida: "Convertida", anulada: "ANULADA", pendiente: "Pendiente", pagada: "Pagada" };

export const CompanyHeader = ({ s, right }) => (
  <div className="flex justify-between items-start gap-6 pb-5 border-b-4" style={{ borderImage: "linear-gradient(90deg,#00B8D4,#7B2CBF) 1" }}>
    <div className="flex gap-4 items-start">
      {s.logo && <img src={s.logo} alt="Logo" className="w-24 h-24 object-contain" data-testid="print-logo" />}
      <div>
        <div className="text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Sora" }}>{s.company_name}</div>
        {s.slogan && <div className="text-sm text-violet-700 font-medium">{s.slogan}</div>}
        <div className="mt-2 text-xs text-slate-600 leading-relaxed">
          {s.legal_id && <div>Cédula jurídica: {s.legal_id}</div>}
          {s.address && <div>{s.address}</div>}
          {s.phones?.length > 0 && <div>Tel: {s.phones.join(" · ")}</div>}
          {s.emails?.length > 0 && <div>{s.emails.join(" · ")}</div>}
        </div>
      </div>
    </div>
    <div className="text-right shrink-0">{right}</div>
  </div>
);

// Documento imprimible (fondo blanco) para proformas y facturas
export const PrintDocument = ({ doc, kind, s }) => {
  const isQ = kind === "quote";
  const cur = doc.currency;
  const c = doc.client || {};
  return (
    <div className="bg-white text-slate-900 p-8 text-[13px]" style={{ fontFamily: "Manrope, sans-serif", width: "100%" }}>
      <CompanyHeader
        s={s}
        right={
          <>
            <div className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Sora" }}>{isQ ? "PROFORMA" : "FACTURA"}</div>
            <div className="mt-1 text-lg font-bold text-violet-700" style={{ fontFamily: "JetBrains Mono" }} data-testid="print-number">{doc.number}</div>
            <div className="mt-2 text-xs text-slate-600 space-y-0.5">
              <div>Emisión: <b>{fmtDate(doc.issue_date)}</b></div>
              <div>{isQ ? "Válida hasta" : "Vence"}: <b>{fmtDate(isQ ? doc.valid_until : doc.due_date)}</b></div>
              <div>Estado: <b className={doc.status === "anulada" ? "text-rose-600" : ""}>{STATUS_LABEL[doc.status]}</b></div>
              {doc.quote_number && <div>Ref. proforma: {doc.quote_number}</div>}
            </div>
          </>
        }
      />

      <div className="mt-5 grid grid-cols-2 gap-4">
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Cliente</div>
          <div className="font-bold text-slate-900">{c.name || "Cliente contado"}</div>
          {c.id_number && <div className="text-xs text-slate-600">Cédula: {c.id_number}</div>}
          {c.phone && <div className="text-xs text-slate-600">Tel: {c.phone}</div>}
          {c.email && <div className="text-xs text-slate-600">{c.email}</div>}
          {c.address && <div className="text-xs text-slate-600">{c.address}</div>}
        </div>
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Condiciones</div>
          <div className="text-xs text-slate-700">Moneda: <b>{cur === "USD" ? "Dólares (USD)" : "Colones (CRC)"}</b>{cur === "USD" && ` · T.C. ₡${doc.exchange_rate}`}</div>
          <div className="text-xs text-slate-700">Forma de pago: <b>{doc.payment_method}</b></div>
          {s.payment_info && <div className="text-xs text-slate-600 mt-1 whitespace-pre-line">{s.payment_info}</div>}
        </div>
      </div>

      <table className="w-full mt-5 border-collapse">
        <thead>
          <tr className="text-white text-[11px] uppercase tracking-wider" style={{ background: "#1E1250" }}>
            <th className="p-2 text-left w-8">#</th><th className="p-2 text-left">Descripción</th><th className="p-2 text-right w-14">Cant.</th>
            <th className="p-2 text-right w-28">Precio unit.</th><th className="p-2 text-right w-16">Desc.</th><th className="p-2 text-center w-12">IVA</th><th className="p-2 text-right w-28">Total</th>
          </tr>
        </thead>
        <tbody>
          {doc.items.map((it, i) => (
            <tr key={i} className="border-b border-slate-200 odd:bg-slate-50">
              <td className="p-2 text-slate-500">{i + 1}</td>
              <td className="p-2">
                <div className="font-semibold">{it.name}</div>
                {(it.code || it.description) && <div className="text-[11px] text-slate-500">{[it.code, it.description].filter(Boolean).join(" · ")}</div>}
              </td>
              <td className="p-2 text-right">{it.qty}</td>
              <td className="p-2 text-right">{money(it.price, cur)}</td>
              <td className="p-2 text-right">{it.discount ? `${it.discount}%` : "—"}</td>
              <td className="p-2 text-center">{it.taxable !== false ? "Sí" : "Exento"}</td>
              <td className="p-2 text-right font-semibold">{money(it.total, cur)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-5 flex justify-between gap-6" style={{ pageBreakInside: "avoid" }}>
        <div className="flex-1 text-xs text-slate-700">
          {doc.notes && (<><div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Notas</div><div className="whitespace-pre-line">{doc.notes}</div></>)}
        </div>
        <div className="w-72 text-sm">
          <div className="flex justify-between py-1"><span className="text-slate-600">Subtotal</span><span>{money(doc.subtotal, cur)}</span></div>
          <div className="flex justify-between py-1"><span className="text-slate-600">Descuento{doc.global_discount ? ` (incl. ${doc.global_discount}% global)` : ""}</span><span>− {money(doc.discount, cur)}</span></div>
          <div className="flex justify-between py-1"><span className="text-slate-600">IVA ({doc.iva}%)</span><span>{money(doc.tax, cur)}</span></div>
          <div className="flex justify-between mt-2 p-3 rounded-lg text-white font-bold text-base" style={{ background: "linear-gradient(90deg,#1E1250,#5B21B6)" }}>
            <span>TOTAL</span><span data-testid="print-total">{money(doc.total, cur)}</span>
          </div>
          {doc.paid_amount > 0 && doc.status !== "anulada" && (
            <>
              <div className="flex justify-between py-1 mt-2"><span className="text-slate-600">Abonado</span><span>{money(doc.paid_amount, cur)}</span></div>
              <div className="flex justify-between py-1 font-bold"><span>Saldo pendiente</span><span data-testid="print-balance">{money(doc.balance, cur)}</span></div>
            </>
          )}
        </div>
      </div>

      {s.terms && (
        <div className="mt-8 pt-4 border-t border-slate-200 text-[11px] text-slate-500 whitespace-pre-line" style={{ pageBreakInside: "avoid" }}>
          <b className="text-slate-700">Términos y condiciones: </b>{s.terms}
        </div>
      )}
      <div className="mt-4 text-center text-[10px] text-slate-400">{s.company_name} · {s.phones?.join(" · ")} · {s.emails?.join(" · ")}</div>
    </div>
  );
};
